/**
 * Video Grid Component
 * Manages layout of local and remote video streams
 */

import React from 'react';
import { VideoTile } from './VideoTile';
import type { VideoGridProps } from '@/api/types/webrtc.types';
import './VideoGrid.css';

export const VideoGrid: React.FC<VideoGridProps> = ({
  localStream,
  peers,
  localDisplayName,
  isAudioEnabled,
  isVideoEnabled,
}) => {
  const peerCount = peers.size;

  // Determine grid layout class based on peer count
  const getGridClass = () => {
    if (peerCount === 0) return 'grid-single';
    if (peerCount === 1) return 'grid-pip'; // Picture-in-Picture
    if (peerCount === 2) return 'grid-two';
    if (peerCount <= 4) return 'grid-four';
    return 'grid-many';
  };

  return (
    <div className={`video-grid ${getGridClass()}`}>
      {/* Remote videos */}
      {peerCount > 0 && (
        <div className="remote-videos">
          {Array.from(peers.values()).map((peer) => (
            <div key={peer.id} className="remote-video-wrapper">
              <VideoTile
                stream={peer.stream}
                displayName={peer.displayName}
                isMuted={false}
                isLocal={false}
                connectionState={peer.connectionState}
                isSpeaking={false}
              />
            </div>
          ))}
        </div>
      )}

      {/* Local video */}
      <div
        className={`local-video-wrapper ${peerCount === 0 ? 'centered' : 'pip'}`}
      >
        <VideoTile
          stream={localStream}
          displayName={localDisplayName}
          isMuted={!isAudioEnabled}
          isLocal={true}
          isSpeaking={false}
        />

        {/* Video disabled indicator */}
        {!isVideoEnabled && (
          <div className="video-disabled-overlay">
            <div className="video-disabled-icon">📹</div>
            <div className="video-disabled-text">Camera Off</div>
          </div>
        )}
      </div>

      {/* No peers placeholder */}
      {peerCount === 0 && (
        <div className="no-peers-message">
          <div className="no-peers-icon">👥</div>
          <div className="no-peers-text">Waiting for others to join...</div>
        </div>
      )}
    </div>
  );
};
